import type { CameraCue, CameraFraming, CameraPlace, CameraState, CameraTarget, RenderObject, SceneCamera, Vec3 } from '@v2/contracts'
import { DEFAULT_CAMERA, TableCamera, focusDistance, type CameraSnapshot } from './camera'

export interface CameraDirectorOptions {
  readonly baseline: boolean
  readonly aspect: number
  readonly reducedMotion: boolean
}

interface ActiveCue {
  readonly cue: CameraCue
  readonly home: CameraSnapshot
  readonly navigationVersion: number
  readonly returnAt: number | null
}

/** Presentation only: no timers, DOM, game commands, or access to unprojected state. */
export class CameraDirector {
  private script: SceneCamera | undefined
  private objects: readonly RenderObject[] = []
  private options: CameraDirectorOptions = { baseline: true, aspect: 1.5, reducedMotion: false }
  private seen = new Set<string>()
  private active: ActiveCue | null = null
  private initialized = false

  constructor(private camera: TableCamera) {}

  /** Baseline callers reset the camera to their new viewer's overview before this call. */
  update(script: SceneCamera | undefined, objects: readonly RenderObject[], now: number, options: CameraDirectorOptions): void {
    this.script = script
    this.objects = objects
    this.options = options
    const cue = script?.cue
    if (options.baseline || !this.initialized) {
      this.initialized = true
      this.active = null
      this.seen.clear()
      if (cue) this.remember(cue.id)
      return
    }

    this.yieldToNavigation()
    if (options.reducedMotion && this.active) this.interrupt()
    const isNew = cue && !this.seen.has(cue.id)
    // Unresolvable, reduced-motion and baseline cues are consumed too. A later
    // resize, reconnect or preference change must not replay a stale occurrence.
    if (cue) this.remember(cue.id)
    if (isNew && !options.reducedMotion) {
      const view = resolveCameraTarget(cue.target, cue.framing, script?.places ?? [], objects, this.camera.state, options.aspect)
      if (view) {
        const home = this.active?.home ?? this.camera.capture()
        this.camera.moveTo(view)
        this.active = {
          cue, home, navigationVersion: this.camera.navigationVersion,
          returnAt: cue.mode === 'hold' ? null : now + 0.5 + bounded(cue.duration, 3, 0, 10),
        }
      }
    }
    if (this.active?.cue.mode === 'hold' && cue?.id !== this.active.cue.id) this.restore()
    else if (this.active?.cue.mode === 'hold' && !resolveCameraTarget(this.active.cue.target, this.active.cue.framing, script?.places ?? [], objects, this.camera.state, options.aspect)) this.restore()
    this.tick(now)
  }

  /** Advance with the same controllable presentation clock as component motion. */
  tick(now: number): void {
    this.yieldToNavigation()
    if (this.active?.returnAt !== null && this.active && now >= this.active.returnAt) this.restore()
  }

  /** A named place is explicit player navigation and always supersedes an automatic cue. */
  goToPlace(id: string): boolean {
    const place = this.script?.places?.find(candidate => candidate.id === id)
    if (!place) return false
    const view = resolveCameraTarget(place.target, place.framing, [], this.objects, this.camera.state, this.options.aspect)
    if (!view) return false
    this.active = null
    this.camera.moveTo(view, this.options.reducedMotion, 'user')
    return true
  }

  /** Call on drag/inspection ownership changes that do not themselves move the camera. */
  interrupt(): void {
    if (this.active && this.camera.navigationVersion === this.active.navigationVersion) this.camera.stop()
    this.active = null
  }

  /** Clear private presentation state when destroying a table. */
  clear(): void {
    this.interrupt()
    this.script = undefined
    this.objects = []
    this.seen.clear()
    this.initialized = false
  }

  private yieldToNavigation(): void {
    if (this.active && this.camera.navigationVersion !== this.active.navigationVersion) this.active = null
  }

  private remember(id: string): void {
    this.seen.add(id)
    // History belongs to one viewer and one running table, not an unbounded log.
    if (this.seen.size > 512) this.seen.delete(this.seen.values().next().value!)
  }

  private restore(): void {
    const active = this.active
    this.active = null
    if (active && this.camera.navigationVersion === active.navigationVersion) {
      this.camera.moveTo(active.home.view, this.options.reducedMotion, active.home.owner)
    }
  }
}

/** Resolve semantic references only from this viewer's current compiled scene. */
export function resolveCameraTarget(target: CameraTarget, framing: CameraFraming | undefined, places: readonly CameraPlace[], objects: readonly RenderObject[], camera: CameraState, aspect: number): CameraState | null {
  if (target.kind === 'place') {
    const place = places.find(candidate => candidate.id === target.id)
    return place ? resolveCameraTarget(place.target, { ...place.framing, ...framing }, [], objects, camera, aspect) : null
  }
  const object = target.kind === 'overview' ? overviewBounds(objects) : target.kind === 'group' ? overviewBounds(objects.filter(object => target.ids.includes(object.id))) : objects.find(candidate => candidate.id === target.id && !candidate.decorative && candidate.layer !== 'foreground')
  if (!object) return null
  const base = target.kind === 'overview' ? DEFAULT_CAMERA : camera
  const rawAzimuth = finite(framing?.azimuth, base.azimuth)
  // Choose the equivalent bearing nearest the visible view, avoiding a full spin.
  const azimuth = camera.azimuth + Math.atan2(Math.sin(rawAzimuth - camera.azimuth), Math.cos(rawAzimuth - camera.azimuth))
  const elevation = bounded(framing?.elevation, Math.max(0.8, base.elevation), 0.35, Math.PI / 2 - 0.015)
  const padding = bounded(framing?.padding, 1, 0.5, 3)
  const fitted = focusDistance(object.size, object.rotation, azimuth, elevation, camera.fov, Math.max(0.1, finite(aspect, 1.5))) * padding
  const distance = bounded(framing?.distance, Math.max(5.5, fitted), 4.5, 160)
  return { ...camera, target: object.position, azimuth, elevation, distance }
}

function overviewBounds(objects: readonly RenderObject[]): Pick<RenderObject, 'position' | 'size' | 'rotation'> | null {
  const table = objects.filter(object => !object.decorative && object.layer !== 'foreground')
  if (!table.length) return null
  const min = [Infinity, Infinity, Infinity], max = [-Infinity, -Infinity, -Infinity]
  for (const object of table) {
    const sin = Math.abs(Math.sin(object.rotation)), cos = Math.abs(Math.cos(object.rotation))
    const size = [object.size[0] * cos + object.size[2] * sin, object.size[1], object.size[0] * sin + object.size[2] * cos]
    for (let axis = 0; axis < 3; axis++) {
      min[axis] = Math.min(min[axis], object.position[axis] - size[axis] / 2)
      max[axis] = Math.max(max[axis], object.position[axis] + size[axis] / 2)
    }
  }
  return {
    position: min.map((value, axis) => (value + max[axis]) / 2) as unknown as Vec3,
    size: min.map((value, axis) => max[axis] - value) as unknown as Vec3,
    rotation: 0,
  }
}

const finite = (value: number | undefined, fallback: number): number => value !== undefined && Number.isFinite(value) ? value : fallback
const bounded = (value: number | undefined, fallback: number, min: number, max: number): number => Math.max(min, Math.min(max, finite(value, fallback)))
